import { types as at, errorAction, types } from "@authzsvc/api-lib";
import { Button, Alert, Result, Space, Spin, Typography } from "antd";
import {
  DownloadOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import {
  addGroupMemberGroups,
  addGroupMemberIdentities,
  deleteGroupMemberGroups,
  deleteGroupMemberIdentities,
  getGroupMemberGroupsWithComments,
  getGroupMemberIdentitiesWithComments,
} from "../../actions/api";
import { getDefaultGroupMembersTableColumns } from "../../constants";
import AddMemberSearchModal from "../common/AddMemberSearchModal";
import GroupMembersView from "./members/GroupMembersView";
import CSVModal from "./members/CSVModal";
import { showErrorNotification, showSuccessNotification } from "../../util";
import EGroupWarning from "./EGroupWarning";
import _ from "lodash";

const GroupMembers = ({
  // props
  matchId,
  selectedGroup,
  refreshMembers,
  canManageGroup,
}) => {
  const dispatch = useDispatch();

  const [blocked, setBlocked] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchValueRef = useRef(searchValue);
  const [filter, setFilter] = useState("contains");
  const filterRef = useRef(filter);
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [isAccessChecked, setIsAccessChecked] = useState(false);

  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  const isReplicaGroup =
    selectedGroup?.syncType === "SlaveWithPlaceholders" ||
    selectedGroup?.syncType === "Slave";

  const isCommentDisabled = isReplicaGroup || !canManageGroup;

  const columns = useMemo(
    () =>
      getDefaultGroupMembersTableColumns(selectedGroup.id, isCommentDisabled),
    [selectedGroup.id, isCommentDisabled]
  );

  const getMembers = useCallback(
    async (
      searchText = searchValueRef.current,
      filterValue = filterRef.current
    ) => {
      setLoading(true);

      const createQueryFilter = (field) => {
        return searchText
          ? { filter: [`${field}:${filterValue}:${searchText}`] }
          : {};
      };

      const [identitiesByUpn, identitiesByDisplayName, groups] =
        await Promise.all([
          dispatch(
            getGroupMemberIdentitiesWithComments(
              matchId,
              createQueryFilter("upn")
            )
          ),
          searchText
            ? dispatch(
                getGroupMemberIdentitiesWithComments(
                  matchId,
                  createQueryFilter("displayName")
                )
              )
            : { payload: { data: [] } },
          dispatch(
            getGroupMemberGroupsWithComments(
              matchId,
              createQueryFilter("displayName")
            )
          ),
        ]);

      const isErrorAction =
        identitiesByUpn?.type ===
          errorAction(types.GET_GROUP_MEMBER_IDENTITIES_WITH_COMMENTS) ||
        (searchText &&
          identitiesByDisplayName?.type ===
            errorAction(types.GET_GROUP_MEMBER_IDENTITIES_WITH_COMMENTS)) ||
        groups?.type ===
          errorAction(types.GET_GROUP_MEMBER_GROUPS_WITH_COMMENTS);

      if (isErrorAction) {
        if (
          identitiesByUpn?.payload?.data?.status === 403 ||
          (searchText &&
            identitiesByDisplayName?.payload?.data?.status === 403) ||
          groups?.payload?.data?.status === 403
        ) {
          setBlocked(true);
        } else {
          const title =
            identitiesByUpn?.payload?.title ||
            (searchText && identitiesByDisplayName?.payload?.title) ||
            groups?.payload?.title ||
            "An error occurred fetching the members";
          const description =
            identitiesByUpn?.payload?.data?.message ||
            (searchText && identitiesByDisplayName?.payload?.data?.message) ||
            groups?.payload?.data?.message ||
            "Please try again later";

          showErrorNotification(title, description);
        }
      } else {
        const totalMembershipCount =
          (identitiesByUpn?.payload?.pagination?.total || 0) +
          (groups?.payload?.pagination?.total || 0);
        const identitiesByUpnData = identitiesByUpn?.payload?.data || [];
        const identitiesByDisplayNameData =
          identitiesByDisplayName?.payload?.data || [];
        const groupsData = groups?.payload?.data || [];

        const mergedIdentities = [
          ...identitiesByUpnData,
          ...identitiesByDisplayNameData,
        ];

        const uniqueIdentitiesMap = new Map();
        mergedIdentities.forEach((identity) => {
          uniqueIdentitiesMap.set(identity.memberIdentity.id, identity);
        });

        const uniqueIdentities = Array.from(uniqueIdentitiesMap.values());

        setMembers([...uniqueIdentities, ...groupsData]);
        setTotal(totalMembershipCount);
        setIsLimitReached(
          identitiesByUpnData.length >= 1000 ||
            identitiesByDisplayNameData.length >= 1000 ||
            groupsData.length >= 1000
        );
      }

      setIsInitialized(true);
      setLoading(false);
      setIsAccessChecked(true);
    },
    [dispatch, matchId]
  );

  useEffect(() => {
    getMembers();
  }, [getMembers, refreshMembers]);

  const handleSearch = useMemo(() => {
    return _.debounce((searchText, filterValue) => {
      if (filterValue !== filterRef.current) setFilter(filterValue);
      getMembers(searchText, filterValue);
    }, 1000);
  }, [getMembers]);

  const handleErrorActions = (result, actionType) => {
    if (result.type === errorAction(actionType)) {
      showErrorNotification(result.payload.title, result.payload.data.message);
      return false;
    }
    return true;
  };

  const onDeleteMembers = async (selectedRows) => {
    const groupId = matchId;

    const deleteActions = [
      {
        type: "identity",
        action: deleteGroupMemberIdentities,
        actionType: types.DELETE_GROUP_MEMBER_IDENTITIES,
      },
      {
        type: "group",
        action: deleteGroupMemberGroups,
        actionType: types.DELETE_GROUP_MEMBER_GROUPS,
      },
    ];

    for (const { type, action, actionType } of deleteActions) {
      const ids = selectedRows
        .filter((row) => row.type === type)
        .map((row) => row.id);
      if (ids.length > 0) {
        const result = await dispatch(action(groupId, ids));
        if (!handleErrorActions(result, actionType)) {
          return;
        }
      }
    }

    setSearchValue("");
    await getMembers("");
  };

  const handleAddMember = async (
    newMember,
    comment = "",
    emailUsedInSearch = null
  ) => {
    const groupId = matchId;

    let identity, group;
    const isIdentity =
      newMember.displayName && (newMember.upn || newMember.unconfirmed);
    const isGroup = newMember.groupIdentifier;
    if (isIdentity) {
      const memberData = {
        id: newMember.id,
        comment: comment || "",
        mailingAddress:
          emailUsedInSearch &&
          !emailUsedInSearch.endsWith("@cern.ch") &&
          !newMember.unconfirmed
            ? emailUsedInSearch
            : null,
      };
      identity = await dispatch(
        addGroupMemberIdentities(groupId, [memberData])
      );
    } else if (isGroup) {
      group = await dispatch(
        addGroupMemberGroups(groupId, [{ id: newMember.id, comment }])
      );
    }

    const isErrorAction =
      identity?.type === errorAction(types.ADD_GROUP_MEMBER_IDENTITIES) ||
      group?.type === errorAction(types.ADD_GROUP_MEMBER_GROUPS);

    if (isErrorAction) {
      const title =
        identity?.payload?.title ||
        group?.payload?.title ||
        "An error occurred adding the member";
      const description =
        identity?.payload?.data?.message ||
        group?.payload?.data?.message ||
        "Please try again later";

      showErrorNotification(title, description);

      return;
    } else {
      if (isIdentity) {
        showSuccessNotification("", "Identities successfully added");
      }
      if (isGroup) {
        showSuccessNotification("", "Groups successfully added");
      }
    }

    await getMembers();
  };

  const handleCreateByEmail = async (email) => {
    const groupId = matchId;
    const emailToUpdate = email?.endsWith("@cern.ch") ? null : email;
    const identityIds = [
      { id: email, comment: "", mailingAddress: emailToUpdate },
    ];
    const createByEmailResponse = await dispatch(
      addGroupMemberIdentities(groupId, identityIds)
    );
    if (
      createByEmailResponse &&
      createByEmailResponse.type === errorAction(at.ADD_GROUP_MEMBER_IDENTITIES)
    ) {
      showErrorNotification(
        createByEmailResponse.payload.title,
        createByEmailResponse.payload.data.message
      );
    } else {
      showSuccessNotification("", "Identity successfully created");
    }
    await getMembers();
  };

  if (!isAccessChecked) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
        <Typography.Text style={{ display: "block", marginTop: 16 }}>
          Loading group members...
        </Typography.Text>
      </div>
    );
  }

  if (blocked) {
    return (
      <Result
        status="403"
        title="You don't have the access rights to view group members."
      />
    );
  }

  const nonAddableMembers = [
    {
      type: "Group",
      identifier: selectedGroup.id,
      message: "A group cannot be a member of itself.",
    },
  ];

  return (
    <>
      <EGroupWarning
        selectedGroup={selectedGroup}
        canManageGroup={canManageGroup}
        showSyncMessage={false}
      />
      {isLimitReached && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined style={{ marginRight: 16 }} />}
          message="Only a limited number of members are displayed. Please refine your search to see more results."
          style={{ marginBottom: 16 }}
        />
      )}
      <GroupMembersView
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        columns={columns}
        dataSource={members}
        total={total}
        extraButtons={
          <>
            <Space>
              <Space.Compact>
                <Button
                  id="export_csv"
                  onClick={() => {
                    setImportModalOpen(true);
                    setIsDownload(true);
                  }}
                  icon={<DownloadOutlined />}
                  type="primary"
                  disabled={members.length === 0}
                >
                  Export
                </Button>
                {!selectedGroup.dynamic && (
                  <Button
                    id="mergeButton"
                    onClick={() => {
                      setImportModalOpen(true);
                      setIsDownload(false);
                    }}
                    icon={<UploadOutlined />}
                    type="primary"
                    disabled={isReplicaGroup || !canManageGroup}
                  >
                    Import
                  </Button>
                )}
              </Space.Compact>
              {!selectedGroup.dynamic && canManageGroup && (
                <AddMemberSearchModal
                  searchValue={searchValue}
                  members={members}
                  nonAddableMembers={nonAddableMembers}
                  isReplicaGroup={isReplicaGroup}
                  onSelect={handleAddMember}
                  onCreateByEmail={handleCreateByEmail}
                  groupId={matchId}
                  onMailingAddressChange={getMembers}
                />
              )}
            </Space>
            <CSVModal
              isModalOpen={isImportModalOpen}
              onClose={() => setImportModalOpen(false)}
              group={selectedGroup}
              getMembers={getMembers}
              isDownload={isDownload}
            />
          </>
        }
        loading={loading || !isInitialized}
        onDelete={onDeleteMembers}
        getMembers={handleSearch}
        disabled={isReplicaGroup || !canManageGroup}
      />
    </>
  );
};

export default GroupMembers;
